/**
 * Build header (logo, navigation, ctaButton) from website/template JSON for website-preview-page.
 */

export interface WebsitePreviewHeaderContext {
  websiteId: string | null;
  templateId: string | null;
  currentPageType: string;
  convertLinkToPageType: (link: string) => string;
}

export interface WebsitePreviewHeaderResult {
  logo: any;
  navigation: any[];
  ctaButton: any;
}

/**
 * Build header preview data (logo, navigation, CTA) from provided website JSON, context, and optional header section.
 * @example
 * buildHeaderFromWebsiteData(sampleJson, sampleContext, sampleHeaderSection)
 * { logo: { image: '/assets/logo.png', alt: 'Logo', text: 'Logo', link: '/' }, navigation: [{ id: 'home', label: 'Home', link: '/preview?websiteId=123', pageType: 'home', active: true }], ctaButton: { text: 'Book Wash', link: '/booking', style: { /* ... */
export function buildHeaderFromWebsiteData(
  jsonData: any,
  context: WebsitePreviewHeaderContext,
  headerSection?: any
): WebsitePreviewHeaderResult {
  let logo: any = { text: 'Logo', link: '/' };
  let navigation: any[] = [];
  let ctaButton: any = { text: 'Book Wash', link: '/booking', style: 'primary' };

  const websiteHeader = jsonData?.website?.header;
  const websiteBranding = jsonData?.website?.branding;
  const id = context.websiteId || context.templateId;
  const idParam = context.websiteId ? 'websiteId' : 'id';

  if (websiteHeader) {
    if (websiteHeader.logo) {
      logo = {
        image: websiteHeader.logo.url || '',
        alt: websiteHeader.logo.alt || 'Logo',
        text: websiteHeader.logo.text || 'Logo',
        link: websiteHeader.logo.link || '/',
        type: websiteHeader.logo.type || 'image',
        style: websiteHeader.logo.style || {}
      };
    }
    if (websiteHeader.navigation && Array.isArray(websiteHeader.navigation)) {
      navigation = websiteHeader.navigation.map((nav: any) => {
        const navLink = nav.link || '#';
        const fullPath =
          navLink === '/' || navLink === '#'
            ? (id ? `/preview?${idParam}=${id}` : navLink)
            : (id ? `/preview?${idParam}=${id}&pageType=${context.convertLinkToPageType(navLink)}` : navLink);
        return {
          id: nav.id || nav.label?.toLowerCase().replace(/\s+/g, '-'),
          label: nav.label || '',
          link: fullPath,
          pageType: context.convertLinkToPageType(navLink),
          active:
            nav.active ||
            (navLink === '/' && context.currentPageType === 'home') ||
            context.convertLinkToPageType(navLink) === context.currentPageType
        };
      });
    }
    if (websiteHeader.cta_button) {
      const cta = websiteHeader.cta_button;
      ctaButton = {
        text: cta.label || cta.text || 'Book Wash',
        label: cta.label || cta.text || 'Book Wash',
        link: cta.link || '/booking',
        backgroundColor: cta.style?.background || cta.style?.background_color || cta.backgroundColor || '#1B5BFF',
        textColor: cta.style?.color || cta.style?.text_color || cta.textColor || '#FFFFFF',
        style: cta.style || {}
      };
    }
  } else if (headerSection?.logo) {
    const logoUrl = headerSection.logo.url || headerSection.logo.image || '';
    logo = {
      image: logoUrl,
      alt: headerSection.logo.alt || 'Logo',
      text: headerSection.logo.text || 'Logo',
      link: headerSection.logo.link || '/'
    };
  }

  if (!logo?.image && websiteBranding?.logo?.url) {
    logo = {
      image: websiteBranding.logo.url,
      alt: websiteBranding.logo.alt || 'Logo',
      text: 'Logo',
      link: '/'
    };
  }

  return { logo, navigation, ctaButton };
}
